import { Box, alpha, useTheme } from "@mui/material";
import { rotuloCompetencia, type Espelho, type EspelhoColaborador } from "@/api/escala.service";
import { formatCNPJ } from "@/utils/cnpjUtils";
import { formatCPF } from "@/utils/cpfUtils";
import { corDaJornada } from "@/components/escala/cargaHoraria";
import {
  COR_FERIADO,
  CORES_IMPRESSAO,
  FAIXA_DOMINGO_IMPRESSAO,
  FAIXA_FERIADO_IMPRESSAO,
  FUNDO_CABECALHO_IMPRESSAO,
  FUNDO_DOMINGO_IMPRESSAO,
} from "@/components/escala/tiposDia";
import { ESCALINHAS_POR_PAGINA, linhasDaEscalinha, paginasDeEscalinhas, type LinhaEscalinha } from "@/components/escala/escalinha";

const ALTURA_LINHA = "4.9mm";
// Matrícula e nome em cima, CPF embaixo.
const ALTURA_IDENTIFICACAO = "7mm";
// Largura de cada parte do horário: hora, "às", hora, "e", hora, "às", hora. Fixas, para o "às" e o
// "e" caírem no mesmo lugar em todas as linhas, com ou sem intervalo.
const PARTES_DO_HORARIO = "2.6em 1.5em 2.6em 1.1em 2.6em 1.5em 2.6em";
const umaLinha = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

interface EscalinhaImpressaoProps {
  espelho: Espelho;
  // Só a escalinha deste colaborador; sem ele, as de todos, na ordem da grade.
  colaboradorId?: string;
  // Tudo em preto, com domingo e feriado em cinza.
  pretoBranco?: boolean;
}

// Contexto que as partes da escalinha dividem.
interface Desenho {
  pretoBranco: boolean;
  primaria: string;
  primariaTexto: string;
  borda: string;
}

// Escala individual para recortar: A4 paisagem, seis por página separadas por linha tracejada,
// sempre 31 linhas. A colorida segue a grade da tela: cada dia no fundo do tipo, o trabalhado no
// verde com a hora na cor da carga, e as faixas de domingo e feriado. Sem carga horária, as colunas
// de entrada e saída viram uma só, "Situação", e o dia trabalhado diz TRABALHO.
export function EscalinhaImpressao({ espelho, colaboradorId, pretoBranco = false }: EscalinhaImpressaoProps) {
  const theme = useTheme();
  const colaboradores = colaboradorId ? espelho.colaboradores.filter((c) => c.id === colaboradorId) : espelho.colaboradores;
  const paginas = paginasDeEscalinhas(colaboradores);
  const desenho: Desenho = {
    pretoBranco,
    primaria: theme.palette.primary.main,
    primariaTexto: theme.palette.primary.contrastText,
    borda: pretoBranco ? "#999" : "#bdbdbd",
  };

  return (
    <Box
      sx={{
        color: "#000",
        bgcolor: "#fff",
        fontFamily: "Arial, Helvetica, sans-serif",
        // Sem isso o Chrome, por padrão, imprime sem os fundos.
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {paginas.map((pagina, i) => (
        <Box
          key={i}
          data-testid="pagina-escalinha"
          sx={{ display: "flex", boxSizing: "border-box", p: "6mm", breakAfter: i < paginas.length - 1 ? "page" : "auto" }}
        >
          {pagina.map((colaborador, j) => (
            <Box
              key={colaborador.id}
              sx={{ width: `${100 / ESCALINHAS_POR_PAGINA}%`, boxSizing: "border-box", px: "1.5mm", borderLeft: j > 0 ? "1px dashed #666" : "none" }}
            >
              <Escalinha espelho={espelho} colaborador={colaborador} desenho={desenho} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// Fonte da matrícula e do nome, para o nome longo caber na largura da escalinha.
const fonteDoNome = (texto: string) => (texto.length <= 30 ? 6 : texto.length <= 36 ? 5.3 : 4.7);

function Escalinha({ espelho, colaborador, desenho }: { espelho: Espelho; colaborador: EspelhoColaborador; desenho: Desenho }) {
  const { gerada } = espelho;
  const linhas = linhasDaEscalinha(espelho, colaborador.id);
  const empresa = gerada.loja_razao_social || gerada.loja_nome;
  const identificacao = [colaborador.matricula, colaborador.nome].filter(Boolean).join(" – ");
  const { pretoBranco } = desenho;

  return (
    <Box component="section" aria-label={`Escalinha de ${colaborador.nome}`}>
      <Box sx={{ textAlign: "center", fontSize: 6, lineHeight: 1.35, mb: "3px", textTransform: "uppercase" }}>
        <Box sx={{ fontWeight: 700 }}>{empresa}</Box>
        {gerada.loja_cnpj && <Box>CNPJ: {formatCNPJ(gerada.loja_cnpj)}</Box>}
        <Box sx={{ fontWeight: 700, color: pretoBranco ? undefined : desenho.primaria }}>
          Escala – {gerada.escala_nome} – {rotuloCompetencia(gerada.ano, gerada.mes)}
        </Box>
      </Box>
      <Box
        component="table"
        sx={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          fontSize: 5.3,
          "& th, & td": {
            border: `1px solid ${desenho.borda}`,
            height: ALTURA_LINHA,
            p: "0 1px",
            textAlign: "center",
            verticalAlign: "middle",
            whiteSpace: "nowrap",
            overflow: "hidden",
          },
        }}
      >
        <colgroup>
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "20.5%" }} />
          <col style={{ width: "20.5%" }} />
          <col style={{ width: "20.5%" }} />
          <col style={{ width: "20.5%" }} />
        </colgroup>
        <thead>
          <tr>
            <Box
              component="th"
              colSpan={6}
              sx={{
                height: `${ALTURA_IDENTIFICACAO} !important`,
                lineHeight: 1.3,
                bgcolor: pretoBranco ? undefined : desenho.primaria,
                color: pretoBranco ? undefined : desenho.primariaTexto,
              }}
            >
              <Box sx={{ fontSize: fonteDoNome(identificacao), textTransform: "uppercase", ...umaLinha }}>{identificacao}</Box>
              <Box sx={{ fontWeight: 400 }}>CPF: {colaborador.cpf ? formatCPF(colaborador.cpf) : "–"}</Box>
            </Box>
          </tr>
          <Box
            component="tr"
            sx={{
              "& th": {
                bgcolor: pretoBranco ? FUNDO_CABECALHO_IMPRESSAO : alpha(desenho.primaria, 0.12),
                color: pretoBranco ? undefined : desenho.primaria,
                fontSize: 4.6,
                textTransform: "uppercase",
              },
            }}
          >
            <th>Dia</th>
            <th>Sem</th>
            {gerada.com_carga_horaria ? (
              <>
                <th>Entrada</th>
                <th>Saída</th>
                <th>Entrada</th>
                <th>Saída</th>
              </>
            ) : (
              <th colSpan={4}>Situação</th>
            )}
          </Box>
        </thead>
        <tbody>
          {linhas.map((linha, i) =>
            linha ? (
              <Linha key={i} linha={linha} desenho={desenho} />
            ) : (
              <tr key={i}>
                <td />
                <td />
                <td colSpan={4} />
              </tr>
            ),
          )}
        </tbody>
      </Box>
    </Box>
  );
}

function Linha({ linha, desenho }: { linha: LinhaEscalinha; desenho: Desenho }) {
  const { pretoBranco } = desenho;
  const faixa = !linha.destaque ? undefined : pretoBranco ? FUNDO_DOMINGO_IMPRESSAO : linha.feriado ? FAIXA_FERIADO_IMPRESSAO : FAIXA_DOMINGO_IMPRESSAO;
  // Na colorida, o dia de domingo e o de feriado vão num círculo, como no cabeçalho da grade.
  const destaque = linha.feriado ? COR_FERIADO : desenho.primaria;
  const tipo = CORES_IMPRESSAO[linha.tipo];

  return (
    <Box component="tr" sx={{ bgcolor: faixa }}>
      <td>
        {linha.destaque && !pretoBranco ? (
          <Box
            sx={{
              mx: "auto",
              width: "3.8mm",
              height: "3.8mm",
              lineHeight: "3.8mm",
              borderRadius: "50%",
              fontWeight: 700,
              bgcolor: destaque,
              color: linha.feriado ? "#fff" : desenho.primariaTexto,
            }}
          >
            {linha.dia}
          </Box>
        ) : (
          linha.dia
        )}
      </td>
      <Box component="td" sx={{ fontWeight: linha.destaque ? 700 : 400, color: linha.destaque && !pretoBranco ? destaque : undefined }}>
        {linha.letra}
      </Box>
      <Box component="td" colSpan={4} sx={{ fontWeight: pretoBranco && linha.tipo === "T" ? 400 : 700 }}>
        {pretoBranco || !tipo || !linha.texto ? (
          linha.horas ? <Horario horas={linha.horas} /> : linha.texto
        ) : (
          // Na colorida, o dia vai no fundo do tipo, como na grade: o trabalhado no verde, com a hora
          // na cor da carga.
          <Box sx={{ mx: "1mm", height: "3.8mm", lineHeight: "3.8mm", borderRadius: "0.9mm", bgcolor: tipo.bg, color: tipo.fg }}>
            {linha.horas ? <Horario horas={linha.horas} cor={corDaJornada(linha.cor)} ligacao={tipo.fg} /> : linha.texto}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// "06 às 10 e 11 às 14:20", cada parte com largura fixa: as horas da entrada encostam no "às" pela
// direita e as da saída pela esquerda. Os espaços nas pontas não aparecem, mas fazem o texto da
// célula sair corrido. ligacao é a cor do "às" e do "e".
function Horario({ horas, cor, ligacao }: { horas: string[]; cor?: string; ligacao?: string }) {
  const [entrada, saida, volta, fim] = horas;
  const partes = [
    { texto: entrada, alinha: "right" },
    { texto: " às ", alinha: "center", ligacao: true },
    { texto: saida, alinha: "left" },
    { texto: volta ? " e " : "", alinha: "center", ligacao: true },
    { texto: volta, alinha: "right" },
    { texto: volta ? " às " : "", alinha: "center", ligacao: true },
    { texto: fim, alinha: "left" },
  ];
  return (
    <Box sx={{ display: "inline-grid", gridTemplateColumns: PARTES_DO_HORARIO, color: cor, verticalAlign: "middle" }}>
      {partes.map((p, i) => (
        <Box component="span" key={i} sx={{ textAlign: p.alinha, ...(p.ligacao && { color: ligacao, fontWeight: 400 }) }}>
          {p.texto}
        </Box>
      ))}
    </Box>
  );
}
